import { of } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { flatMap, catchError } from 'rxjs/operators';
import { Epic, ofType } from 'redux-observable';
import { getOptionsObject } from '../../utils/api';
import { FETCH_RI_SCENARIO, fetchRIScenarioSuccess, fetchRIScenarioError, UPDATE_RI_SCENARIO } from './actions';
import { setInitialStateForContributions } from '../contribution/actions';
import { setInitialStateForSpouseData } from '../spouse/actions';
import { setInitialStateForRetirementAge } from '../retirement-age/actions';
import { setInitialStateForRetirementIncomeGoal } from '../retirement-income-goal/actions';
import { setInitialStateForSocialSecurity } from '../social-security/actions';
import { setInitialStateForAccounts, setAccountHolderOptions } from '../account/actions';
import {setInitialStateForIncomeEvents} from '../event/actions';
import { getAge, getDateObject, changeCase, getPreciseValue } from 'utils/helpers';
import { accountTypeCodes } from 'utils/constants';
import { setInitialFutureIncludeInScenario } from 'redux/future-income-expenses/actions';

const handleResponse = (data: any) => {
  const response = data.response ? data.response : data.payload;
  const {
    accounts,
    events,
    incomeGoal,
    incomeGoalUnit,
    retirementStartAge,
    retirementEndAge,
    spouseProfile,
    spouseRetirementStartAge,
    spouseRetirementEndAge,
    participantProfile,
    socialSecurityStartAge,
    spouseSocialSecurityStartAge,
    socialSecurityAmount,
    spouseSocialSecurityAmount,
    compensations,
  } = response;

  let participantSalary = 0,
    spouseSalary = 0,
    spouseCompensationId = null;
  let contributions: Array<Record<any, any>> = [];
  const accountsData: Array<Record<any, any>> = [];
  const eventsData: Array<Record<any, any>> = [];
  const hasSpouse = spouseProfile && spouseProfile.bpId ? true : false;

  if (compensations) {
    compensations.forEach((item: any) => {
      if (item.bpId === participantProfile?.bpId) {
        participantSalary = item.amount;
      }
      if (item.bpId === spouseProfile?.bpId) {
        spouseSalary = item.amount;
        spouseCompensationId = item.id;
      }
    });
  }
  const isRetirementNeedOverridden = incomeGoalUnit === 'DOL' ? true : false;
  const retirementIncomeGoal = isRetirementNeedOverridden ? incomeGoal : null;

  const accountHolderOptions: Record<any, any> = [];
  if (participantProfile) {
    accountHolderOptions.push({
      name: `${participantProfile.firstName} ${participantProfile.lastName}`,
      value: participantProfile.bpId,
    });
  }
  if (spouseProfile) {
    accountHolderOptions.push({
      name: `${spouseProfile.firstName} ${spouseProfile.lastName}`,
      value: spouseProfile.bpId,
    });
  }

  if (accounts) {
    accounts.forEach((account: any, i: number) => {
      const {
        yodleeAgreementId,
        id,
        assetAllocation,
        overrideAccountTypeCode,
        balance,
        bpId,
        accountCategory,
        contribution,
        displayType,
      } = account;
      if (yodleeAgreementId) {
        let hasYodleeAccountTypeError = false,
          hasYodleeAssetAllocationError = false,
          isOverriddenYodleeAccount = false;
        if (id) {
          isOverriddenYodleeAccount = true;
        } else {
          if (!assetAllocation || assetAllocation === null) {
            hasYodleeAssetAllocationError = true;
          } else {
            const totalAllocationPercent = Object.values(assetAllocation).reduce(
              (accum: any, current: any) => accum + getPreciseValue(current * 100),
              0,
            );
            hasYodleeAssetAllocationError = totalAllocationPercent === 100 ? false : true;
          }
          if (overrideAccountTypeCode === accountTypeCodes.UNKN || overrideAccountTypeCode === accountTypeCodes.NS || overrideAccountTypeCode === null) {
            hasYodleeAccountTypeError = true;
          }
        }

        accountsData.push({
          ...account,
          accountIndex: i,
          balance: Math.trunc(balance),
          hasYodleeAssetAllocationError,
          hasYodleeAccountTypeError,
          hasYodleeError: hasYodleeAssetAllocationError || hasYodleeAccountTypeError,
          isOverriddenYodleeAccount,
          isYodleeAccount: yodleeAgreementId ? true : false,
          isEditableAccount: id ? true : false,
          accountHolder: accountHolderOptions.find((option: Record<any, any>) => option.value === bpId).name,
          assetAllocation: assetAllocation
            ? {
                stockPercent: getPreciseValue(assetAllocation.stockPercent * 100),
                bondsPercent: getPreciseValue(assetAllocation.bondsPercent * 100),
                moneyMarketPercent: getPreciseValue(assetAllocation.moneyMarketPercent * 100),
                otherPercent: getPreciseValue(assetAllocation.otherPercent * 100),
              }
            : null,
          displayType: hasYodleeAccountTypeError ? '' : changeCase(displayType),
        });
      } else {
        accountsData.push({
          ...account,
          accountIndex: i,
          balance: Math.trunc(balance),
          isManualAccount: accountCategory === 'Manual',
          isEditableAccount: id ? true : false,
          accountHolder: accountHolderOptions.find((option: any) => option.value === bpId).name,
          assetAllocation: assetAllocation
            ? {
                stockPercent: getPreciseValue(assetAllocation.stockPercent * 100),
                bondsPercent: getPreciseValue(assetAllocation.bondsPercent * 100),
                moneyMarketPercent: getPreciseValue(assetAllocation.moneyMarketPercent * 100),
                otherPercent: getPreciseValue(assetAllocation.otherPercent * 100),
              }
            : null,
          annualSavingsEE: contribution
            ? contribution.find((contribution: any) => contribution.id === 'EE')
              ? contribution.find((contribution: any) => contribution.id === 'EE').amount
              : null
            : null,
          annualSavingsER: contribution
            ? contribution.find((contribution: any) => contribution.id === 'ER')
              ? contribution.find((contribution: any) => contribution.id === 'ER').amount
              : null
            : null,
        });
      }
    });
  }

  if (events) {
    events.forEach((event: any, i: number) => {
      const {
        id,
        overrideAccountTypeCode,
        balance,
        bpId,
        typeName,
        unit,
        frequency,
        categoryName,
      } = event;

      eventsData.push({
        ...event,
        eventIndex: i,
        balance: Math.trunc(balance),
        isEditableEvent: id ? true : false,
        accountHolder: accountHolderOptions.find((option: Record<any, any>) => option.value === bpId).name,
        unit,
        frequency,
        typeName,
        categoryName
      });
  })
}


  // if (events) {
  //   const noOfAccounts = accountsData.length;
  //   events.forEach((event: any, i: number) => {
  //     if (event.categoryName === accountTypeCodes.PENSION) {
  //       accountsData.push({
  //         ...event,
  //         accountIndex: noOfAccounts + i,
  //         displayType: event.typeName,
  //         overrideAccountTypeCode: accountTypeCodes.PENSION,
  //         isPensionAccount: true,
  //         amount: Math.trunc(event.amount),
  //         accountHolder: accountHolderOptions.find((option: any) => option.value === event.bpId).name,
  //         displayGroup: 'Outside Retirement Accounts',
  //       });
  //     }
  //   });
  // }

  if (accounts) {
    contributions = accounts
      .filter(
        (account: any) =>
          account.accountCategory === 'RPSAccountSummary' || account.accountCategory === 'TRACAccountSummary',
      )
      .filter((account: any) => account.status === 'ACTIVE')
      .filter(
        (account: any) =>
          account.displayAccountCategory !== 'RPSNQAccountSummary' &&
          account.displayAccountCategory !== 'NonQualAccountSummary' &&
          account.displayAccountCategory !== 'RPSNQTRACAccountSummary',
      )
      .map((account: any, i: number) => {
        let totalContributionPercent = 0,
          totalContributionPercentCatchUp = 0,
          totalContributionAmount = 0,
          totalContributionAmountCatchUp = 0,
          //eslint-disable-next-line
          listOfCatchUpUnits: any = [];
        const units: Array<string> = [];
        const tempContribution =
          account.contribution &&
          account.contribution.map((contribution: any) => {
            units.push(contribution.unit);
            const tempAmount =
              contribution.unit === 'PCT' ? getPreciseValue(contribution.amount * 100) : contribution.amount;

            totalContributionPercent =
              totalContributionPercent +
              (contribution.unit === 'PCT' &&
              contribution.id !== '$' &&
              contribution.id !== '#' &&
              contribution.inputType !== 'B'
                ? tempAmount
                : 0);
            totalContributionPercentCatchUp =
              totalContributionPercentCatchUp +
              (contribution.unit === 'PCT' &&
              (contribution.id === '$' || contribution.id === '#') &&
              contribution.inputType !== 'B'
                ? tempAmount
                : 0);

            totalContributionAmount =
              totalContributionAmount +
              (contribution.unit === 'DOL' &&
              contribution.id !== '$' &&
              contribution.id !== '#' &&
              contribution.inputType !== 'B'
                ? tempAmount
                : 0);
            totalContributionAmountCatchUp =
              totalContributionAmountCatchUp +
              (contribution.unit === 'DOL' &&
              (contribution.id === '$' || contribution.id === '#') &&
              contribution.inputType !== 'B'
                ? tempAmount
                : 0);

            (contribution.id === '$' || contribution.id === '#') &&
              contribution.inputType !== 'B' &&
              listOfCatchUpUnits.push(contribution.unit);

            return {
              ...contribution,
              amount: tempAmount,
            };
          });
        const planLevelUnit = units.every(u => u === 'PCT') ? 'PCT' : units.every(u => u === 'DOL') ? 'DOL' : 'N/A';
        return {
          ...account,
          contribution: tempContribution,
          totalContributionPercent,
          totalContributionPercentCatchUp,
          totalContributionAmount,
          totalContributionAmountCatchUp,
          listOfCatchUpUnits,
          planMaxContributionLimit: getPreciseValue(account.planMaxAmountPercent * 100),
          planLevelUnit,
        };
      });
  }

  return [
    fetchRIScenarioSuccess(response),
    hasSpouse
      ? setInitialStateForSpouseData({
          ...spouseProfile,
          spouseRetirementStartAge,
          spouseRetirementEndAge,
          retirementEndAge,
          currentSalary: spouseSalary,
          spouseHasCompensation: spouseSalary > 0 ? true : false,
          spouseSocialSecurityStartAge,
          birthDateObj: getDateObject(spouseProfile.birthDate),
          spouseCompensationId,
        })
      : setInitialStateForSpouseData({
          firstName: '',
          lastName: '',
          birthDate: '',
          gender: '',
          currentSalary: '',
          spouseRetirementStartAge,
          spouseRetirementEndAge,
          retirementEndAge,
          spouseSocialSecurityStartAge: spouseSocialSecurityStartAge ? spouseSocialSecurityStartAge : 70,
          birthDateObj: getDateObject(''),
          spouseCompensationId,
        }),
    setInitialStateForContributions({ contributions }),
    setInitialStateForAccounts({ accounts: accountsData }),
    setInitialStateForIncomeEvents({ events: eventsData }),
    setInitialFutureIncludeInScenario({events: eventsData}),
    setAccountHolderOptions({ accountHolderOptions }),
    setInitialStateForRetirementAge({
      spouseRetirementStartAge,
      spouseRetirementEndAge,
      retirementEndAge,
      retirementStartAge,
      hasSpouse,
      spouseCurrentAge: hasSpouse ? getAge(spouseProfile.birthDate) : null,
    }),
    setInitialStateForRetirementIncomeGoal({
      incomeGoal: retirementIncomeGoal ? Math.trunc(retirementIncomeGoal) : null,
      participantSalary: Math.trunc(participantSalary),
      spouseSalary: Math.trunc(spouseSalary),
      hasSpouse,
    }),
    setInitialStateForSocialSecurity({
      socialSecurityStartAge: socialSecurityStartAge ? socialSecurityStartAge : 70,
      socialSecurityAmount: socialSecurityAmount,
      spouseSocialSecurityStartAge: spouseSocialSecurityStartAge ? spouseSocialSecurityStartAge : 70,
      spouseSocialSecurityAmount: spouseSocialSecurityAmount,
      hasSpouse,
    }),
  ];
};

export const fetchRIScenario: Epic = action$ =>
  action$.pipe(
    ofType(FETCH_RI_SCENARIO),
    flatMap(() =>
      ajax(getOptionsObject('/retirement-income-ui/service/ri-scenario'))
        .pipe(flatMap(handleResponse))
        .pipe(catchError(ex => of(fetchRIScenarioError(ex)))),
    ),
  );

export const updateRIScenario: Epic = action$ => action$.pipe(ofType(UPDATE_RI_SCENARIO), flatMap(handleResponse));
